// Nidalee VFX — two-phase ability:
//   NidaleeSpearVFX: javelin arcs from caster to target with a tip glow and green impact burst.
//   NidaleePounceVFX: drives the unit sprite in a leaping arc, with an orange tiger-claw slash on landing.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

// ── Spear ────────────────────────────────────────────────────────────────────

export const NIDALEE_SPEAR_TRAVEL_MS = 420;
const SPEAR_IMPACT_MS = 220;
const SPEAR_TOTAL = NIDALEE_SPEAR_TRAVEL_MS + SPEAR_IMPACT_MS;

export class NidaleeSpearVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  onEnd?: () => void;

  constructor(
    private from: Point,
    private to: Point,
    private hexSize: number,
    private onImpact: () => void,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, SPEAR_TOTAL);
    if (!this.impactFired && this.elapsed >= NIDALEE_SPEAR_TRAVEL_MS) {
      this.impactFired = true;
      this.onImpact();
    }
    if (this.elapsed >= SPEAR_TOTAL) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < NIDALEE_SPEAR_TRAVEL_MS) {
      this.drawSpear(ctx);
    } else {
      this.drawImpact(ctx, (this.elapsed - NIDALEE_SPEAR_TRAVEL_MS) / SPEAR_IMPACT_MS);
    }
  }

  private getSpearTransform(ms: number): { x: number; y: number; angle: number } {
    const t  = Easing.easeOutQuad(Math.min(ms / NIDALEE_SPEAR_TRAVEL_MS, 1));
    const x  = this.from.x + (this.to.x - this.from.x) * t;
    const arc = Math.sin(t * Math.PI * 0.72) * this.hexSize * 0.65;
    const y  = this.from.y + (this.to.y - this.from.y) * t - arc;

    // Tangent angle — use a small look-ahead sample
    const t2   = Easing.easeOutQuad(Math.min((ms + 18) / NIDALEE_SPEAR_TRAVEL_MS, 1));
    const x2   = this.from.x + (this.to.x - this.from.x) * t2;
    const arc2 = Math.sin(t2 * Math.PI * 0.72) * this.hexSize * 0.65;
    const y2   = this.from.y + (this.to.y - this.from.y) * t2 - arc2;
    const angle = Math.atan2(y2 - y, x2 - x);

    return { x, y, angle };
  }

  private drawSpear(ctx: CanvasRenderingContext2D): void {
    const { x, y, angle } = this.getSpearTransform(this.elapsed);
    const t    = this.elapsed / NIDALEE_SPEAR_TRAVEL_MS;
    const L    = this.hexSize * 3.2;   // long javelin — very readable as a spear
    const tipH = this.hexSize * 0.55;  // wide leaf blade
    const W    = 7;                    // shaft thickness

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Shaft — layered for depth: dark core + lighter edge
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "#5a3d14";
    ctx.shadowColor = "#c8a040";
    ctx.shadowBlur = 4;
    ctx.fillRect(-L / 2, -W / 2, L - tipH, W);

    // Lighter grain highlight along top edge of shaft
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = "#b8893a";
    ctx.shadowBlur = 0;
    ctx.fillRect(-L / 2, -W / 2, L - tipH, W * 0.28);

    // Butt cap — small dark nub at the tail
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = "#3a2a0a";
    ctx.beginPath();
    ctx.moveTo(-L / 2, -W / 2 + 1);
    ctx.lineTo(-L / 2 - 6, 0);
    ctx.lineTo(-L / 2, W / 2 - 1);
    ctx.closePath();
    ctx.fill();

    // Broad leaf-shaped metal blade — yellow-green, clearly distinct from the shaft
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#aad830";
    ctx.shadowColor = "#ccee44";
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.moveTo(L / 2, 0);                   // very tip
    ctx.lineTo(L / 2 - tipH, -(W + 5));     // upper shoulder
    ctx.lineTo(L / 2 - tipH * 0.4, -W / 2); // where blade meets shaft
    ctx.lineTo(L / 2 - tipH * 0.4, W / 2);
    ctx.lineTo(L / 2 - tipH, (W + 5));      // lower shoulder
    ctx.closePath();
    ctx.fill();

    // Blade centre vein — brighter line down the middle for readability
    ctx.globalAlpha = 0.85;
    ctx.strokeStyle = "#eeff88";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#aaff22";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(L / 2 - tipH * 0.4, 0);
    ctx.lineTo(L / 2 - 2, 0);
    ctx.stroke();

    // Bright glow at the very tip
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "#eeff88";
    ctx.shadowColor = "#88ff22";
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(L / 2, 0, 4, 0, Math.PI * 2);
    ctx.fill();

    // Green energy trail behind the butt — fades over travel
    const trailAlpha = 0.35 * (1 - t * 0.7);
    ctx.globalAlpha = trailAlpha;
    ctx.fillStyle = "#88cc44";
    ctx.shadowColor = "#aadd44";
    ctx.shadowBlur = 10;
    ctx.fillRect(-L / 2 - L * 0.5, -3, L * 0.55, 6);

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - t;
    const cx = this.to.x;
    const cy = this.to.y;

    ctx.save();

    // Inner flash
    if (t < 0.3) {
      const flash = 1 - t / 0.3;
      ctx.globalAlpha = flash * 0.7;
      ctx.fillStyle = "#ddff88";
      ctx.shadowColor = "#aafa44";
      ctx.shadowBlur = 32;
      ctx.beginPath();
      ctx.arc(cx, cy, this.hexSize * 0.65 * flash, 0, Math.PI * 2);
      ctx.fill();
    }

    // Outer ring
    const r = this.hexSize * (0.4 + t * 1.9);
    ctx.globalAlpha = fade * 0.82;
    ctx.strokeStyle = "#88ee44";
    ctx.lineWidth = 3 - t * 2;
    ctx.shadowColor = "#aafa44";
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary smaller ring
    const r2 = this.hexSize * (0.2 + t * 1.1);
    ctx.globalAlpha = fade * 0.5;
    ctx.strokeStyle = "#ccff66";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r2, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= SPEAR_TOTAL;
  }
}

// ── Pounce ───────────────────────────────────────────────────────────────────

const POUNCE_DASH_MS   = 290;
const POUNCE_IMPACT_MS = 340;
const POUNCE_TOTAL     = POUNCE_DASH_MS + POUNCE_IMPACT_MS;

export class NidaleePounceVFX implements Animation {
  private elapsed = 0;
  private biteFired = false;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private targetPx: Point,
    private hexSize: number,
    private onBite: () => void,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, POUNCE_TOTAL);

    if (this.elapsed <= POUNCE_DASH_MS) {
      const t    = Easing.easeInOutCubic(this.elapsed / POUNCE_DASH_MS);
      this.visual.x = this.startPx.x + (this.targetPx.x - this.startPx.x) * t;
      // Arc upward during the leap
      const arc  = Math.sin(t * Math.PI) * this.hexSize * 1.0;
      this.visual.y = this.startPx.y + (this.targetPx.y - this.startPx.y) * t - arc;
    }

    if (!this.biteFired && this.elapsed >= POUNCE_DASH_MS) {
      this.biteFired = true;
      this.onBite();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < POUNCE_DASH_MS) {
      this.drawLeapTrail(ctx, this.elapsed / POUNCE_DASH_MS);
    } else {
      this.drawClashImpact(ctx, (this.elapsed - POUNCE_DASH_MS) / POUNCE_IMPACT_MS);
    }
  }

  private drawLeapTrail(ctx: CanvasRenderingContext2D, t: number): void {
    const curX = this.visual.x;
    const curY = this.visual.y;
    const dx = curX - this.startPx.x;
    const dy = curY - this.startPx.y;
    const trailLen = Math.sqrt(dx * dx + dy * dy);
    if (trailLen < 2) return;

    const angle = Math.atan2(dy, dx);
    const fade  = 1 - t * 0.5;

    ctx.save();
    ctx.translate(this.startPx.x, this.startPx.y);
    ctx.rotate(angle);

    // Wide orange glow
    ctx.globalAlpha = fade * 0.38;
    ctx.fillStyle = "#ff8820";
    ctx.shadowColor = "#ffaa44";
    ctx.shadowBlur = 18;
    ctx.fillRect(0, -this.hexSize * 0.46, trailLen, this.hexSize * 0.92);

    // Bright green core streak
    ctx.globalAlpha = fade * 0.72;
    ctx.fillStyle = "#88dd44";
    ctx.shadowColor = "#aaee55";
    ctx.shadowBlur = 10;
    ctx.fillRect(0, -this.hexSize * 0.14, trailLen, this.hexSize * 0.28);

    // Stripe accents (tiger-stripe feel)
    const stripeCount = 4;
    for (let i = 0; i < stripeCount; i++) {
      const sx = (trailLen * i) / stripeCount;
      const sw = trailLen / (stripeCount * 2.5);
      ctx.globalAlpha = fade * 0.22;
      ctx.fillStyle = "#ff5500";
      ctx.fillRect(sx, -this.hexSize * 0.32, sw, this.hexSize * 0.64);
    }

    ctx.restore();
  }

  private drawClashImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const cx    = this.targetPx.x;
    const cy    = this.targetPx.y;
    const fade  = 1 - Easing.easeInQuad(t);
    const baseAngle = Math.atan2(
      this.targetPx.y - this.startPx.y,
      this.targetPx.x - this.startPx.x,
    );

    ctx.save();

    // Initial orange burst flash
    if (t < 0.28) {
      const flash = 1 - t / 0.28;
      ctx.globalAlpha = flash * 0.65;
      ctx.fillStyle = "#ff9933";
      ctx.shadowColor = "#ffdd44";
      ctx.shadowBlur = 32;
      ctx.beginPath();
      ctx.arc(cx, cy, this.hexSize * 0.9 * flash, 0, Math.PI * 2);
      ctx.fill();
    }

    // Three tiger-claw slash marks radiating toward target direction
    const clawSpreads = [-0.38, 0, 0.38];
    const clawLen = this.hexSize * 1.55 * Easing.easeOutCubic(t);
    for (const spread of clawSpreads) {
      const clawAngle = baseAngle + spread;
      const ex = cx + Math.cos(clawAngle) * clawLen;
      const ey = cy + Math.sin(clawAngle) * clawLen;
      ctx.globalAlpha = fade * 0.88;
      ctx.strokeStyle = "#ff9933";
      ctx.lineWidth   = 3.5 * (1 - t * 0.7) + 0.5;
      ctx.shadowColor = "#ffdd44";
      ctx.shadowBlur  = 16;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
    }

    // Expanding outer ring
    const ringR = this.hexSize * 0.35 + t * this.hexSize * 1.6;
    ctx.globalAlpha = fade * 0.52;
    ctx.strokeStyle = "#ddaa44";
    ctx.lineWidth   = 2;
    ctx.shadowColor = "#ff9933";
    ctx.shadowBlur  = 10;
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= POUNCE_TOTAL;
  }
}
